import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
  Grid,
  styled,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import axiosInstance from '../../lib/axios';
import { OrganizationSubscriptionTab } from '../../components/subscriptions/OrganizationSubscriptionTab';
import { OrganizationModulesTab } from '../../components/subscriptions/OrganizationModulesTab';
import { OrganizationLimitsTab } from '../../components/subscriptions/OrganizationLimitsTab';
import { TierBadge } from '../../components/subscription/TierBadge';
import { useOrganizationLimits } from '../../hooks/subscriptions/useOrganizationLimits';
import { useOrgSubscription } from '../../hooks/subscriptions/useOrgSubscription';
import { SubscriptionTier } from '../../types/subscription';

interface Organization {
  id: string;
  name: string;
  contactEmail: string;
  contactPhone?: string;
  address?: string;
  createdAt: string;
  updatedAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`org-tabpanel-${index}`}
      aria-labelledby={`org-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 4 }}>{children}</Box>}
    </div>
  );
}

const StyledCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(to bottom right, #ffffff, #fafbfc)',
  borderRadius: '16px',
  boxShadow: '0 2px 16px rgba(0, 0, 0, 0.04)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
  },
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0891b2 0%, #6366f1 100%)',
  borderRadius: '20px',
  padding: '40px',
  marginBottom: '32px',
  boxShadow: '0 12px 40px rgba(8, 145, 178, 0.25)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-10%',
    width: '40%',
    height: '200%',
    background: 'linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.03), transparent)',
    transform: 'rotate(15deg)',
    pointerEvents: 'none',
  },
}));

const InfoCard = styled(Card)(({ theme }) => ({
  background: '#ffffff',
  borderRadius: '12px',
  padding: '20px',
  height: '100%',
  border: '1px solid #e3e8ef',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    borderColor: '#0891b2',
  },
}));

export const OrganizationShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState(0);

  // Sync tab with URL parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 3) {
        setCurrentTab(tabIndex);
      }
    }
  }, [searchParams]);

  const { data: organization, isLoading, error } = useQuery<Organization>({
    queryKey: ['organization', id],
    queryFn: async () => {
      const response = await axiosInstance.get(`/organizations/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  // Fetch organization limits including tier
  const { data: organizationLimits } = useOrganizationLimits(id || '');

  // Fetch active subscription to get accurate tier from plan
  const { data: subscription } = useOrgSubscription(id || '');

  // Use subscription's plan tier if available, otherwise fall back to organizationLimits.tier
  const displayTier = subscription?.plan?.tier || organizationLimits?.tier;

  // Fetch all organizations for the selector
  const { data: organizations, isLoading: isLoadingOrgs } = useQuery<Organization[]>({
    queryKey: ['organizations'],
    queryFn: async () => {
      const response = await axiosInstance.get('/organizations');
      // Handle both array response and paginated response structure
      return Array.isArray(response.data) ? response.data : response.data.data || [];
    },
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setSearchParams({ tab: newValue.toString() });
  };

  const handleOrganizationChange = (_event: React.SyntheticEvent, newValue: Organization | null) => {
    if (newValue) {
      // Navigate to the new organization while preserving the current tab
      navigate(`/organizations/show/${newValue.id}?tab=${currentTab}`);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: '#0891b2' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ borderRadius: '12px' }}>
          {error instanceof Error ? error.message : 'Failed to load organization'}
        </Alert>
      </Box>
    );
  }

  if (!organization) {
    return (
      <Box>
        <Alert severity="info" sx={{ borderRadius: '12px' }}>
          Organization not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <HeaderCard>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '16px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                <BusinessIcon sx={{ fontSize: 32, color: '#fff' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '32px',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {organization.name}
                  </Typography>
                  {displayTier && (
                    <TierBadge
                      tier={displayTier as SubscriptionTier}
                      size="medium"
                      showTooltip={true}
                    />
                  )}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '14px',
                  }}
                >
                  Organization Details & Management
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Autocomplete<Organization>
                value={organization || null}
                onChange={handleOrganizationChange}
                options={organizations || []}
                loading={isLoadingOrgs}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Switch Organization"
                    size="small"
                  />
                )}
                sx={{
                  minWidth: 320,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    padding: '6px 12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    border: '2px solid rgba(255, 255, 255, 0.9)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: '#ffffff',
                      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                      border: '2px solid #ffffff',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
                      border: '2px solid #ffffff',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiInputLabel-root': {
                    color: '#475569',
                    fontWeight: 600,
                    '&.Mui-focused': {
                      color: '#0891b2',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#1e293b',
                    fontWeight: 600,
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#64748b',
                  },
                }}
              />
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/organizations')}
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  backdropFilter: 'blur(10px)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#fff',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  },
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/organizations/edit/${id}`)}
                sx={{
                  background: '#ffffff',
                  color: '#0891b2',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
                  '&:hover': {
                    background: '#f8f9fa',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
                  },
                }}
              >
                Edit
              </Button>
            </Box>
          </Box>
        </Box>
      </HeaderCard>

      <StyledCard>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: '2px solid #e3e8ef',
            px: 3,
            pt: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '15px',
              color: '#697386',
              minHeight: 48,
              transition: 'all 0.2s ease',
              '&.Mui-selected': {
                color: '#0891b2',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#0891b2',
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab label="Overview" />
          <Tab label="Subscription" />
          <Tab label="Modules" />
          <Tab label="Resource Limits" />
        </Tabs>

        <CardContent sx={{ p: 4 }}>
          {/* Details Tab */}
          <TabPanel value={currentTab} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InfoCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #0891b2 0%, #6366f1 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <EmailIcon sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#697386',
                          fontSize: '12px',
                          textTransform: 'uppercase',
                          fontWeight: 700,
                          letterSpacing: '0.5px',
                        }}
                      >
                        Contact Email
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#1a1f36',
                          fontWeight: 600,
                          fontSize: '16px',
                        }}
                      >
                        {organization.contactEmail}
                      </Typography>
                    </Box>
                  </Box>
                </InfoCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <InfoCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PhoneIcon sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#697386',
                          fontSize: '12px',
                          textTransform: 'uppercase',
                          fontWeight: 700,
                          letterSpacing: '0.5px',
                        }}
                      >
                        Contact Phone
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#1a1f36',
                          fontWeight: 600,
                          fontSize: '16px',
                          fontFamily: 'monospace',
                        }}
                      >
                        {organization.contactPhone || 'Not provided'}
                      </Typography>
                    </Box>
                  </Box>
                </InfoCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <InfoCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <LocationOnIcon sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#697386',
                          fontSize: '12px',
                          textTransform: 'uppercase',
                          fontWeight: 700,
                          letterSpacing: '0.5px',
                        }}
                      >
                        Address
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#1a1f36',
                          fontWeight: 600,
                          fontSize: '16px',
                        }}
                      >
                        {organization.address || 'Not provided'}
                      </Typography>
                    </Box>
                  </Box>
                </InfoCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <InfoCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CalendarTodayIcon sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#697386',
                          fontSize: '12px',
                          textTransform: 'uppercase',
                          fontWeight: 700,
                          letterSpacing: '0.5px',
                        }}
                      >
                        Created Date
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#1a1f36',
                          fontWeight: 600,
                          fontSize: '16px',
                        }}
                      >
                        {new Date(organization.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </Typography>
                    </Box>
                  </Box>
                </InfoCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Subscription Tab */}
          <TabPanel value={currentTab} index={1}>
            <OrganizationSubscriptionTab
              organizationId={id!}
              organizationName={organization.name}
            />
          </TabPanel>

          {/* Modules Tab */}
          <TabPanel value={currentTab} index={2}>
            <OrganizationModulesTab
              organizationId={id!}
              organizationName={organization.name}
            />
          </TabPanel>

          {/* Resource Limits Tab */}
          <TabPanel value={currentTab} index={3}>
            <OrganizationLimitsTab
              organizationId={id!}
              organizationName={organization.name}
            />
          </TabPanel>
        </CardContent>
      </StyledCard>
    </Box>
  );
};
